// frontend/src/features/customFields/CustomFieldsManagerPage.tsx
// ADR-095 — Custom Fields Framework
//
// AM-009 update (closes OQ-CF-01): the hardcoded KNOWN_TARGET_TYPES list
// (with its "TODO(integration)" comment) is replaced by a real call to
// GET /api/v1/custom-field-definitions/target-types/, backed by the
// shared.entity_search custom-fieldable registry (AM-009). Persian labels
// for known keys are kept client-side (the backend registry intentionally
// carries no label — see AM-009 Rule AM-009-03); any key the backend
// returns that isn't in this local label map still renders, just with its
// raw "app_label.model" string as a fallback label.

import { useEffect, useState, useCallback } from "react";
import {
  listCustomFieldDefinitions,
  listCustomFieldTargetTypes,
  createCustomFieldDefinition,
  updateCustomFieldDefinition,
  deactivateCustomFieldDefinition,
  activateCustomFieldDefinition,
} from "./api";
import type { CustomFieldDefinition, CustomFieldType } from "./types";
import { RuleBuilder, RuleGroup, createEmptyGroup, ruleGroupToJsonLogic, jsonLogicToRuleGroup } from "./components/RuleBuilder";

// AM-009: labels only — the authoritative list of *which* keys are valid
// now comes from the backend (listCustomFieldTargetTypes). If the backend
// adds a fifth registered model later, it will still show up here with a
// raw-key fallback label until someone adds a friendly Persian name below.
const TARGET_TYPE_LABELS: Record<string, string> = {
  "inventory.materialfamily": "متریال (Material)",
  "inventory.warehouse": "انبار (Warehouse)",
  "production.productionorder": "سفارش تولید (Production Order)",
  "quality.qualitycheckrevision": "چک لیست کیفیت (Quality Check)",
  // AM-032: DowntimeRecord lives in the SEPARATE "apps.production.shopfloor"
  // Django app (AppConfig.label = "production_shopfloor"), not "production" —
  // see AM-032 doc §2.1. Using "production.downtimerecord" here would never
  // match the real ContentType key and this label would silently never show.
  "production_shopfloor.downtimerecord": "توقف تولید (Downtime)",
};

const FIELD_TYPE_OPTIONS: { value: CustomFieldType; label: string }[] = [
  { value: "text", label: "متن کوتاه" },
  { value: "textarea", label: "متن بلند" },
  { value: "number", label: "عدد" },
  { value: "checkbox", label: "بله/خیر" },
  { value: "date", label: "تاریخ" },
  { value: "select", label: "انتخابی (لیست گزینه‌ها)" },
  { value: "file", label: "فایل" },
  { value: "image", label: "تصویر" },
];

import { PageBanner } from "@/shared/components/PageBanner";

export function CustomFieldsManagerPage() {
  const [targetTypes, setTargetTypes] = useState<string[]>([]);
  const [targetTypesLoading, setTargetTypesLoading] = useState(true);
  const [targetTypesError, setTargetTypesError] = useState<string | null>(null);
  const [targetType, setTargetType] = useState<string>("");
  const [definitions, setDefinitions] = useState<CustomFieldDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingDefinition, setEditingDefinition] = useState<CustomFieldDefinition | null>(null);

  // AM-009: load the real, backend-validated list of target types once.
  useEffect(() => {
    let cancelled = false;
    setTargetTypesLoading(true);
    setTargetTypesError(null);
    listCustomFieldTargetTypes()
      .then((types) => {
        if (cancelled) return;
        setTargetTypes(types);
        if (types.length > 0) {
          setTargetType((current) => current || types[0]!);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) setTargetTypesError(err.message);
      })
      .finally(() => {
        if (!cancelled) setTargetTypesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    if (!targetType) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listCustomFieldDefinitions(targetType);
      setDefinitions(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [targetType]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggleActive = async (definition: CustomFieldDefinition) => {
    try {
      if (definition.is_active) {
        await deactivateCustomFieldDefinition(definition.id);
      } else {
        await activateCustomFieldDefinition(definition.id);
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  // KPI Stats
  const totalFields = definitions.length;
  const activeFields = definitions.filter(d => d.is_active).length;
  const requiredFields = definitions.filter(d => d.required).length;
  const conditionalFields = definitions.filter(d => d.conditions && Object.keys(d.conditions).length > 0).length;

  return (
    <div
      dir="rtl"
      style={{
        padding: "0.5rem 0",
        maxWidth: "100%",
        margin: "0",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        fontFamily: "var(--font-family-fa, sans-serif)",
      }}
    >
      <style>{`
        .kpi-card {
          padding: 0.5rem 0.5rem 0.5rem 4rem;
          min-height: 110px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 16px;
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 0.1rem;
          position: relative;
          overflow: hidden;
        }
        .kpi-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.15);
          border-color: rgba(0, 114, 112, 0.3);
        }
      `}</style>

      <PageBanner
        title="مدیریت فیلدهای سفارشی (Custom Fields)"
        subtitle="تعریف فیلد اضافی روی موجودیت‌های سیستم، بدون نیاز به تغییر در کد"
        icon="🛠️"
        helpLink="/help/forms/custom-fields"
      >
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
          <button
            type="button"
            onClick={() => {
              setEditingDefinition(null);
              setShowCreateForm((v) => !v);
            }}
            disabled={!targetType}
            style={{
              padding: "0.6rem 1.2rem",
              borderRadius: "12px",
              background: showCreateForm && !editingDefinition ? "rgba(255, 255, 255, 0.15)" : "#ffffff",
              color: showCreateForm && !editingDefinition ? "#ffffff" : "#007270",
              border: showCreateForm && !editingDefinition ? "1px solid rgba(255, 255, 255, 0.3)" : "1px solid rgba(255, 255, 255, 0.8)",
              fontWeight: 700,
              fontSize: "0.9rem",
              cursor: targetType ? "pointer" : "not-allowed",
              boxShadow: showCreateForm && !editingDefinition ? "none" : "0 4px 12px rgba(0, 0, 0, 0.15)",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "all 0.2s ease",
            }}
          >
            <span>{showCreateForm && !editingDefinition ? "✕" : "➕"}</span>
            <span>{showCreateForm && !editingDefinition ? "بستن فرم" : "افزودن فیلد جدید"}</span>
          </button>
        </div>
      </PageBanner>

      {/* KPI CARDS */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1.5rem",
        }}
      >
        <div className="brand-card kpi-card">
          <span style={{ fontSize: "1.8rem", background: "rgba(0,114,112,0.08)", width: "45px", height: "45px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            🗂️
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              کل فیلدهای سفارشی
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.2rem", fontWeight: "800", color: "#007270" }}>
                {loading ? "..." : totalFields}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#10B981" }}>ثبت شده</span>
            </div>
          </div>
        </div>

        <div className="brand-card kpi-card">
          <span style={{ fontSize: "1.8rem", background: "rgba(16,185,129,0.08)", width: "45px", height: "45px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            ✅
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              فیلدهای فعال
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.2rem", fontWeight: "800", color: "#10B981" }}>
                {loading ? "..." : activeFields}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#10B981" }}>در حال استفاده</span>
            </div>
          </div>
        </div>

        <div className="brand-card kpi-card">
          <span style={{ fontSize: "1.8rem", background: "rgba(245,158,11,0.08)", width: "45px", height: "45px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            📌
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              فیلدهای الزامی
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.2rem", fontWeight: "800", color: "#D4AF37" }}>
                {loading ? "..." : requiredFields}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#F59E0B" }}>ضروری</span>
            </div>
          </div>
        </div>

        <div className="brand-card kpi-card">
          <span style={{ fontSize: "1.8rem", background: "rgba(59,130,246,0.08)", width: "45px", height: "45px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            🔀
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              فیلدهای شرطی
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.2rem", fontWeight: "800", color: "#3B82F6" }}>
                {loading ? "..." : conditionalFields}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#3B82F6" }}>وابسته</span>
            </div>
          </div>
        </div>
      </div>

      {targetTypesError && (
        <div style={{ padding: "0.85rem 1.1rem", borderRadius: "10px", background: "rgba(239, 68, 68, 0.12)", border: "1px solid #EF4444", color: "#B91C1C", fontSize: "0.85rem", fontWeight: 600 }}>
          خطا در بارگذاری فهرست نوع موجودیت‌ها: {targetTypesError}
        </div>
      )}

      {error && <div style={{ padding: "0.85rem 1.1rem", borderRadius: "10px", background: "rgba(239, 68, 68, 0.12)", border: "1px solid #EF4444", color: "#B91C1C", fontSize: "0.85rem", fontWeight: 600 }}>خطا: {error}</div>}

      {/* CONTROL BAR (FILTER TABS) */}
      <div
        className="brand-card-extand"
        style={{
          padding: "1rem 1.25rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", background: "rgba(0,0,0,0.04)", padding: "4px", borderRadius: "12px", flexWrap: "wrap" }}>
          {targetTypesLoading && <span style={{ fontSize: "0.85rem", color: "#666", padding: "0 8px" }}>در حال بارگذاری موجودیت‌ها...</span>}
          {!targetTypesLoading && targetTypes.length === 0 && (
            <span style={{ fontSize: "0.85rem", color: "#666", padding: "0 8px" }}>هیچ نوع موجودیتی یافت نشد.</span>
          )}
          {targetTypes.map((t) => (
            <button
              key={t}
              onClick={() => setTargetType(t)}
              style={{
                padding: "6px 14px",
                borderRadius: "8px",
                fontSize: "0.85rem",
                fontWeight: 700,
                border: "none",
                background: targetType === t ? "#007270" : "transparent",
                color: targetType === t ? "#FFFFFF" : "var(--color-text-main, #2B2B2B)",
                boxShadow: targetType === t ? "0 2px 8px rgba(0,114,112,0.25)" : "none",
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "all 0.2s",
              }}
            >
              🗂️ {TARGET_TYPE_LABELS[t] ?? t}
            </button>
          ))}
        </div>
      </div>

      {showCreateForm && targetType && (
        <CreateDefinitionForm
          targetType={targetType}
          existingKeys={definitions.map((d) => d.field_key)}
          editingDefinition={editingDefinition}
          onCreated={async () => {
            setShowCreateForm(false);
            setEditingDefinition(null);
            await load();
          }}
          onCancel={() => {
            setShowCreateForm(false);
            setEditingDefinition(null);
          }}
        />
      )}

      {/* FIELDS GRID VIEW */}
      <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "3rem", color: "#64748B" }}>
            <div style={{ fontSize: "1.8rem" }}>🔄</div>
            <p style={{ marginTop: "0.5rem", fontWeight: 600 }}>در حال بارگذاری فیلدها...</p>
          </div>
        ) : definitions.length === 0 ? (
          <div className="brand-card-extand" style={{ textAlign: "center", padding: "3rem" }}>
            <p style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--color-text-main, #2B2B2B)" }}>هیچ فیلد سفارشی‌ای تعریف نشده است.</p>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1rem" }}>
            {definitions.map((def) => (
              <div
                key={def.id}
                className="brand-card-extand"
                style={{
                  padding: "1.25rem",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  border: "1px solid var(--color-border, #E4DCC8)",
                  borderRadius: "14px",
                  background: def.is_active ? "#FFFFFF" : "var(--color-surface, #F5F1E6)",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                }}
              >
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                    <span style={{ fontSize: "0.75rem", fontFamily: "monospace", padding: "2px 8px", background: "rgba(0,0,0,0.05)", borderRadius: "6px", color: "#555", direction: "ltr", fontWeight: 700 }}>
                      {def.field_key}
                    </span>
                    <span style={{ fontSize: "0.75rem", fontWeight: 700, padding: "2px 8px", borderRadius: "12px", background: def.is_active ? "rgba(16, 185, 129, 0.15)" : "rgba(107, 114, 128, 0.15)", color: def.is_active ? "#10B981" : "#6B7280" }}>
                      {def.is_active ? "● فعال" : "غیرفعال"}
                    </span>
                  </div>

                  <h3 style={{ margin: "0 0 0.5rem 0", fontSize: "1.1rem", fontWeight: 800, color: "var(--color-text-main, #2B2B2B)", display: "flex", alignItems: "center", gap: "6px" }}>
                    <span>✏️</span>
                    <span>{def.label}</span>
                  </h3>

                  <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginTop: "1rem", fontSize: "0.85rem", color: "#555" }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <strong>نوع فیلد:</strong>
                      <span>{FIELD_TYPE_OPTIONS.find((f) => f.value === def.field_type)?.label ?? def.field_type}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <strong>الزامی:</strong>
                      <span>{def.required ? "بله" : "خیر"}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <strong>شروط نمایش:</strong>
                      <span>
                        {def.conditions && Object.keys(def.conditions).length > 0 ? (
                          <span style={{ background: "rgba(59, 130, 246, 0.1)", color: "#2563EB", padding: "2px 6px", borderRadius: "4px", fontSize: "0.75rem" }}>وابسته</span>
                        ) : (
                          "همیشه نمایان"
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1.25rem", paddingTop: "1rem", borderTop: "1px solid var(--color-border, #E4DCC8)" }}>
                  <button
                    type="button"
                    onClick={() => handleToggleActive(def)}
                    style={{ padding: "6px 12px", borderRadius: "8px", fontSize: "0.8rem", fontWeight: 700, cursor: "pointer", border: "none", background: "transparent", color: def.is_active ? "#EF4444" : "#10B981" }}
                  >
                    {def.is_active ? "غیرفعال کن" : "فعال کن"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingDefinition(def);
                      setShowCreateForm(true);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    style={{ padding: "6px 14px", borderRadius: "8px", fontSize: "0.8rem", fontWeight: 700, cursor: "pointer", border: "1px solid #007270", background: "#007270", color: "#FFFFFF" }}
                  >
                    ویرایش فیلد
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface CreateDefinitionFormProps {
  targetType: string;
  existingKeys: string[];
  editingDefinition?: CustomFieldDefinition | null;
  onCreated: () => void;
  onCancel: () => void;
}

function CreateDefinitionForm({ targetType, existingKeys, editingDefinition, onCreated, onCancel }: CreateDefinitionFormProps) {
  const [fieldKey, setFieldKey] = useState("");
  const [label, setLabel] = useState("");
  const [fieldType, setFieldType] = useState<CustomFieldType>("text");
  const [required, setRequired] = useState(false);
  const [helpText, setHelpText] = useState("");
  const [optionsList, setOptionsList] = useState<{ value: string, label: string }[]>([]);
  const [ruleGroup, setRuleGroup] = useState<RuleGroup>(createEmptyGroup());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (editingDefinition) {
      setFieldKey(editingDefinition.field_key);
      setLabel(editingDefinition.label);
      setFieldType(editingDefinition.field_type);
      setRequired(editingDefinition.required);
      setHelpText(editingDefinition.help_text);
      setOptionsList(editingDefinition.options || []);
      setRuleGroup(jsonLogicToRuleGroup(editingDefinition.conditions));
    } else {
      setFieldKey("");
      setLabel("");
      setFieldType("text");
      setRequired(false);
      setHelpText("");
      setOptionsList([]);
      setRuleGroup(createEmptyGroup());
    }
  }, [editingDefinition]);

  const keyPattern = /^[a-z][a-z0-9_]*$/;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!editingDefinition) {
      if (!keyPattern.test(fieldKey)) {
        setError("کلید فیلد باید snake_case لاتین باشد (مثلاً hs_code).");
        return;
      }
      if (existingKeys.includes(fieldKey)) {
        setError("این کلید قبلاً برای همین نوع موجودیت استفاده شده است.");
        return;
      }
    }

    const options = fieldType === "select" ? optionsList.filter(o => o.value.trim() !== "") : [];

    if (fieldType === "select" && options.length === 0) {
      setError("برای نوع «انتخابی» حداقل یک گزینه لازم است (قالب: value:برچسب, ...).");
      return;
    }

    let parsedConditions = undefined;
    try {
      parsedConditions = ruleGroupToJsonLogic(ruleGroup);
    } catch (err) {
      setError("خطا در تبدیل شروط گرافیکی به JsonLogic");
      return;
    }

    setSaving(true);
    try {
      if (editingDefinition) {
        await updateCustomFieldDefinition(editingDefinition.id, {
          label,
          required,
          help_text: helpText,
          options,
          conditions: parsedConditions,
        });
      } else {
        await createCustomFieldDefinition({
          target_content_type: targetType,
          field_key: fieldKey,
          label,
          field_type: fieldType,
          required,
          help_text: helpText,
          options,
          conditions: parsedConditions,
          display_order: existingKeys.length,
        });
      }
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="brand-card-extand"
      style={{ padding: "1.5rem", border: "1px solid var(--color-border, #E4DCC8)", marginBottom: "1.5rem" }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "0.75rem", marginBottom: "1.5rem", borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
        <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#007270" }}>
          {editingDefinition ? "✏️ ویرایش فیلد سفارشی" : "➕ ایجاد فیلد سفارشی جدید"}
        </h3>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem" }}>
        <div>
          <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>کلید فیلد (snake_case)</label>
          <input
            style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: editingDefinition ? "rgba(0,0,0,0.05)" : "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: editingDefinition ? "not-allowed" : "text", direction: "ltr" }}
            value={fieldKey}
            onChange={(e) => setFieldKey(e.target.value)}
            disabled={!!editingDefinition}
            placeholder="hs_code"
          />
        </div>
        <div>
          <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>برچسب نمایشی</label>
          <input
            style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="کد تعرفهٔ گمرکی"
          />
        </div>
        <div>
          <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>نوع فیلد</label>
          <select
            style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: editingDefinition ? "rgba(0,0,0,0.05)" : "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: editingDefinition ? "not-allowed" : "pointer" }}
            value={fieldType}
            onChange={(e) => setFieldType(e.target.value as CustomFieldType)}
            disabled={!!editingDefinition}
          >
            {FIELD_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "1.5rem" }}>
          <input
            type="checkbox"
            checked={required}
            onChange={(e) => setRequired(e.target.checked)}
            style={{ width: "18px", height: "18px", cursor: "pointer", accentColor: "#007270" }}
          />
          <span style={{ fontSize: "0.85rem", color: "#2B2B2B", fontWeight: 700, cursor: "pointer" }} onClick={() => setRequired(!required)}>الزامی</span>
        </div>

        <div style={{ gridColumn: "1 / -1" }}>
          <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>متن راهنما (اختیاری)</label>
          <input
            style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
            value={helpText}
            onChange={(e) => setHelpText(e.target.value)}
          />
        </div>

        {fieldType === "select" && (
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "8px" }}>گزینه‌های لیست انتخابی</label>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {optionsList.map((opt, index) => (
                <div key={index} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    style={{ flex: "0 0 30%", padding: "6px 10px", borderRadius: "8px", border: "1px solid var(--color-border, #E4DCC8)", fontSize: "0.85rem", outline: "none", direction: "ltr" }}
                    placeholder="مقدار (Value)"
                    value={opt.value}
                    onChange={(e) => {
                      const newList = [...optionsList];
                      const item = newList[index];
                      if (item) item.value = e.target.value;
                      setOptionsList(newList);
                    }}
                  />
                  <input
                    style={{ flex: "1", padding: "6px 10px", borderRadius: "8px", border: "1px solid var(--color-border, #E4DCC8)", fontSize: "0.85rem", outline: "none" }}
                    placeholder="برچسب نمایشی (Label)"
                    value={opt.label}
                    onChange={(e) => {
                      const newList = [...optionsList];
                      const item = newList[index];
                      if (item) item.label = e.target.value;
                      setOptionsList(newList);
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setOptionsList(optionsList.filter((_, i) => i !== index))}
                    style={{ background: "none", border: "none", color: "#EF4444", cursor: "pointer", fontSize: "1.1rem", padding: "0 4px" }}
                    title="حذف گزینه"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setOptionsList([...optionsList, { value: "", label: "" }])}
                style={{ alignSelf: "flex-start", background: "none", border: "none", color: "#007270", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer", marginTop: "4px" }}
              >
                + افزودن گزینه
              </button>
            </div>
          </div>
        )}

        <div style={{ gridColumn: "1 / -1", borderTop: "1px solid var(--color-border, #E4DCC8)", paddingTop: "1.25rem", marginTop: "0.5rem" }}>
          <label style={{ fontSize: "0.9rem", fontWeight: 800, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>
            شروط نمایش (اختیاری)
          </label>
          <p style={{ fontSize: "0.8rem", color: "#666", marginBottom: "12px" }}>
            اگر هیچ شرطی تعریف نکنید، این فیلد همیشه نمایش داده می‌شود. می‌توانید فیلدهای دیگر مثل <code>material_type</code> را در بخش فیلد وارد کنید.
          </p>
          <RuleBuilder
            group={ruleGroup}
            onChange={setRuleGroup}
            availableFields={existingKeys}
          />
        </div>
      </div>

      {error && <div style={{ marginTop: "1rem", padding: "0.75rem", borderRadius: "10px", background: "rgba(239, 68, 68, 0.12)", border: "1px solid #EF4444", color: "#B91C1C", fontSize: "0.85rem", fontWeight: 600 }}>{error}</div>}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "1.5rem", paddingTop: "1rem", borderTop: "1px solid var(--color-border, #E4DCC8)" }}>
        {editingDefinition && (
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            style={{ padding: "8px 16px", borderRadius: "10px", background: "none", border: "1px solid var(--color-border, #E4DCC8)", color: "#555", fontSize: "0.85rem", fontWeight: 700, cursor: saving ? "not-allowed" : "pointer" }}
          >
            لغو ویرایش
          </button>
        )}
        <button
          type="submit"
          disabled={saving}
          style={{ padding: "8px 16px", borderRadius: "10px", background: "#007270", color: "#FFF", border: "none", fontSize: "0.85rem", fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}
        >
          {saving ? "در حال ذخیره…" : editingDefinition ? "ذخیره تغییرات" : "ایجاد فیلد"}
        </button>
      </div>
    </form>
  );
}
